import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Autocomplete,
    Box,
} from '@mui/material';
import { createValdIntegration, verifyValdCredentials } from '../integrations.api';
import { CreateValdIntegrationRequest, ValdTenant } from '../integrations.types';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import { Organization } from 'utils';

interface CreateIntegrationDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const CreateIntegrationDialog: React.FC<CreateIntegrationDialogProps> = ({
    open,
    onClose,
    onSuccess,
}) => {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [availableTenants, setAvailableTenants] = useState<ValdTenant[]>([]);
    const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
    const [isLoadingTenants, setIsLoadingTenants] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [orgSearchInput, setOrgSearchInput] = useState('');

    const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
    const [selectedTenant, setSelectedTenant] = useState<ValdTenant | null>(null);
    const [selectedRegion, setSelectedRegion] = useState<'aue' | 'use' | 'euw'>('use');

    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const searchOrganizations = useCallback(async (searchTerm: string) => {
        setIsLoadingOrgs(true);
        try {
            const response = await adminGetAllOrganizations(searchTerm, 50);
            setOrganizations(response.data.data);
        } catch (error) {
            console.error('Failed to fetch organizations', error);
            setError('Failed to load organizations. Please try again.');
        } finally {
            setIsLoadingOrgs(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            searchOrganizations('');
            fetchAvailableTenants();
        }
    }, [open, searchOrganizations]);

    useEffect(() => {
        if (!open) return;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            searchOrganizations(orgSearchInput);
        }, 300);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [orgSearchInput, open, searchOrganizations]);

    const fetchAvailableTenants = async () => {
        setIsLoadingTenants(true);
        try {
            const response = await verifyValdCredentials();
            setAvailableTenants(response.data.remote_tenants || []);
        } catch (error) {
            console.error('Failed to fetch available tenants', error);
            setError('Failed to load available tenants. Please check VALD credentials.');
        } finally {
            setIsLoadingTenants(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);

        try {
            if (!selectedOrganization || !selectedTenant) {
                setError('Please select both an organization and a tenant.');
                setIsSubmitting(false);
                return;
            }

            const payload: CreateValdIntegrationRequest = {
                organization_uuid: selectedOrganization.uuid,
                tenant_id: selectedTenant.id,
                region: selectedRegion,
            };

            await createValdIntegration(payload);
            onSuccess();
            onClose();

            // Reset form
            setSelectedOrganization(null);
            setSelectedTenant(null);
            setSelectedRegion('use');
            setOrgSearchInput('');
        } catch (error: any) {
            console.error('Failed to create integration', error);
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.errors?.organization_uuid?.[0] ||
                error?.response?.data?.errors?.tenant_id?.[0] ||
                'Failed to create integration. Please try again.';
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>Create New VALD Integration</DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                        <Autocomplete
                            options={organizations}
                            getOptionLabel={(option) => `${option.name} (${option.uuid})`}
                            loading={isLoadingOrgs}
                            value={selectedOrganization}
                            onChange={(event, newValue) => setSelectedOrganization(newValue)}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'reset') {
                                    setOrgSearchInput(newInputValue);
                                }
                            }}
                            filterOptions={(x) => x}
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Organization *"
                                    placeholder="Type to search organizations..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoadingOrgs ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <Autocomplete
                            options={availableTenants}
                            getOptionLabel={(option) =>
                                option.name ? `${option.name} (${option.id})` : option.id
                            }
                            loading={isLoadingTenants}
                            value={selectedTenant}
                            onChange={(event, newValue) => setSelectedTenant(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="VALD Tenant *"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoadingTenants ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <FormControl fullWidth>
                            <InputLabel id="region-label">Region *</InputLabel>
                            <Select
                                labelId="region-label"
                                value={selectedRegion}
                                label="Region *"
                                onChange={(e) =>
                                    setSelectedRegion(e.target.value as 'aue' | 'use' | 'euw')
                                }
                            >
                                <MenuItem value="use">United States East (use)</MenuItem>
                                <MenuItem value="aue">Australia East (aue)</MenuItem>
                                <MenuItem value="euw">Europe West (euw)</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={
                            isSubmitting ? <CircularProgress size={20} color="inherit" /> : null
                        }
                    >
                        {isSubmitting ? 'Creating...' : 'Create Integration'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CreateIntegrationDialog;
